import type { ProvisioningArtwork } from '../types/modpackProvisioning';

export type ArtworkImportResult = { artwork: ProvisioningArtwork } | {
  artwork: null; available: false; reason: 'not_found' | 'unsupported_provider' | 'unavailable';
};
export type ArtworkSelectionStatus = { busy: 'import' | 'upload' | null; message: string | null; error: string | null };

export function artworkProject(source: string): string | null {
  const match = /^curseforge:([1-9]\d*)@[1-9]\d*$/.exec(source);
  return match ? `curseforge:${match[1]}` : null;
}

/** Keeps optional lookup results separate from staff-selected artwork. */
export function createArtworkSelection(options: {
  initial?: ProvisioningArtwork;
  importArtwork: (source: string, signal: AbortSignal) => Promise<ArtworkImportResult>;
  uploadArtwork: (file: File, signal: AbortSignal) => Promise<{ artwork: ProvisioningArtwork }>;
  onChange: (artwork: ProvisioningArtwork | undefined) => void;
  onStatus: (status: ArtworkSelectionStatus) => void;
}) {
  let artwork = options.initial;
  let manual = !!artwork;
  let disposed = false;
  let project: string | null | undefined;
  let latestSource = '';
  let pending: AbortController | null = null;
  let status: ArtworkSelectionStatus = { busy: null, message: null, error: null };
  const results = new Map<string, { artwork?: ProvisioningArtwork; message: string }>();
  const publish = (next: ArtworkSelectionStatus) => { status = next; options.onStatus(next); };
  const change = (value?: ProvisioningArtwork) => { artwork = value; options.onChange(value); };
  const cancel = () => { pending?.abort(); pending = null; };
  const current = (controller: AbortController) => !disposed && pending === controller && !controller.signal.aborted;
  async function selectSource(source: string) {
      latestSource = source;
      if (disposed || manual || status.busy === 'upload') return;
      const nextProject = artworkProject(source);
      if (project === nextProject) return;
      project = nextProject;
      cancel();
      if (artwork) change();
      if (!project) {
        publish({ busy: null, message: source ? 'Upload artwork for this source.' : null, error: null });
        return;
      }
      const cached = results.get(project);
      if (cached) {
        if (cached.artwork) change(cached.artwork);
        publish({ busy: null, message: cached.message, error: null });
        return;
      }
      const selectedProject = project;
      const controller = new AbortController(); pending = controller;
      publish({ busy: 'import', message: 'Looking for artwork in Apogee…', error: null });
      try {
        const result = await options.importArtwork(source, controller.signal);
        if (!current(controller)) return;
        const message = result.artwork ? 'Artwork from Apogee.' : result.reason === 'not_found'
          ? 'No artwork found in Apogee. Upload an image.' : 'Apogee artwork is unavailable. Upload an image.';
        results.set(selectedProject, { artwork: result.artwork ?? undefined, message });
        if (result.artwork) change(result.artwork);
        publish({ busy: null, message, error: null });
      } catch {
        if (!current(controller)) return;
        const message = 'Apogee artwork is unavailable. Upload an image.';
        results.set(selectedProject, { message });
        publish({ busy: null, message, error: null });
      } finally { if (pending === controller) pending = null; }
  }
  return {
    selectSource,
    async upload(file: File) {
      if (disposed || status.busy === 'upload') return;
      const error = !file.size || file.size > 8 * 1024 * 1024 ? 'Choose an image up to 8 MiB.'
        : !/\.(png|jpe?g|webp|gif)$/i.test(file.name) ? 'Choose a PNG, JPEG, WebP or GIF image.' : null;
      if (error) { publish({ ...status, error }); return; }
      cancel();
      const controller = new AbortController(); pending = controller;
      publish({ busy: 'upload', message: null, error: null });
      try {
        const result = await options.uploadArtwork(file, controller.signal);
        if (!current(controller)) return;
        manual = true;
        change(result.artwork);
        publish({ busy: null, message: null, error: null });
      } catch (failure) {
        if (!current(controller)) return;
        publish({ busy: null, message: null, error: failure instanceof Error ? failure.message : 'Artwork upload failed. Try the same image again.' });
      } finally {
        if (pending === controller) pending = null;
        if (!disposed && !manual) await selectSource(latestSource);
      }
    },
    dispose() { disposed = true; cancel(); },
  };
}
